import React, { useRef, useState } from "react";
import { Upload, FileDown, Database, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { http, API } from "@/lib/api";
import { useFilters } from "@/state/FilterContext";
import { toast } from "sonner";

export default function DataUploadCard() {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const { dataStatus, refreshStatus } = useFilters();

  const onPick = () => fileRef.current?.click();

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await http.post("/dashboard/upload-csv", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`${res.data.imported} sevkiyat içe aktarıldı`);
      if (res.data.errors?.length) {
        toast.warning(`${res.data.errors.length} satır atlandı`);
      }
      refreshStatus();
      // Trigger global re-fetch by reloading page (simple, reliable)
      setTimeout(() => window.location.reload(), 600);
    } catch (err) {
      const msg = err.response?.data?.detail?.message || err.response?.data?.detail || "Yükleme başarısız";
      toast.error(typeof msg === "string" ? msg : "Yükleme başarısız");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const resetDemo = async () => {
    try {
      await http.post("/dashboard/reset-demo");
      toast.success("Demo verilere döndü");
      refreshStatus();
      setTimeout(() => window.location.reload(), 600);
    } catch {
      toast.error("Sıfırlama başarısız");
    }
  };

  return (
    <div
      data-testid="data-upload-card"
      className="bg-white border border-slate-200 rounded-lg p-5 fade-up"
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-md bg-[#E5F0FA] flex items-center justify-center flex-shrink-0">
            <Database className="w-5 h-5 text-[#0070F2]" />
          </div>
          <div>
            <h3 className="font-display text-base font-semibold text-slate-800">Veri Kaynağı</h3>
            <p className="text-sm text-slate-600 mt-0.5">
              Mevcut: <span className="font-semibold tabular-nums">{dataStatus.count}</span> sevkiyat
              <span className={`ml-2 text-[11px] uppercase tracking-[0.08em] font-semibold px-1.5 py-0.5 rounded ${
                dataStatus.is_demo ? "bg-amber-50 text-amber-700" : "bg-emerald-50 text-emerald-700"
              }`}>
                {dataStatus.is_demo ? "Demo" : "Yüklenmiş"}
              </span>
            </p>
            <p className="text-xs text-slate-500 mt-2 max-w-xl leading-relaxed">
              Kendi SAP TM verinizi CSV olarak yükleyin. Sütunlar: <code className="bg-slate-100 px-1 rounded">id, date, source_name, carrier_name, mode, weight_tons, cost_try, on_time, status</code>.
            </p>
          </div>
        </div>
        <div className="flex flex-col gap-2 flex-shrink-0">
          <input ref={fileRef} type="file" accept=".csv" hidden onChange={onFile} data-testid="csv-file-input" />
          <Button
            data-testid="upload-csv-btn"
            onClick={onPick}
            disabled={uploading}
            className="bg-[#0070F2] hover:bg-[#0054E3] text-white"
            size="sm"
          >
            <Upload className="w-4 h-4 mr-1.5" />
            {uploading ? "Yükleniyor…" : "CSV Yükle"}
          </Button>
          <a
            data-testid="download-template"
            href={`${API}/dashboard/csv-template`}
            className="inline-flex items-center justify-center text-sm text-[#0054E3] hover:bg-slate-50 border border-slate-300 rounded-md px-3 py-1.5 font-medium transition-colors"
          >
            <FileDown className="w-4 h-4 mr-1.5" /> Şablon İndir
          </a>
          {!dataStatus.is_demo && (
            <Button
              data-testid="reset-demo-btn"
              onClick={resetDemo}
              variant="ghost"
              size="sm"
              className="text-slate-600"
            >
              <RefreshCw className="w-4 h-4 mr-1.5" /> Demoya Dön
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
